'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, BookHeart, Check, Crown, Star } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface MontessoriUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  isSignedIn: boolean;
}

export default function MontessoriUpsellModal({
  isOpen,
  onClose,
  isSignedIn,
}: MontessoriUpsellModalProps) {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen && mounted) {
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      
      // Prevent scroll
      document.body.style.overflow = 'hidden';
      
      // Prevent layout shift by adding padding for scrollbar width
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen, mounted]);

  if (!isOpen || !mounted) return null;

  const handleUpgrade = () => {
    router.push('/pricing');
    onClose();
  };

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'white',
          borderRadius: '20px',
          padding: '1.5rem',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(0, 0, 0, 0.05)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
          }}
          aria-label="Close"
        >
          <X size={20} color="#64748b" />
        </button>

        {/* Icon */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            margin: '0 auto 1rem auto',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #8BC34A, #CDDC39)',
            boxShadow: '0 8px 24px rgba(139, 195, 74, 0.3)',
          }}
        >
          <BookHeart size={32} color="white" />
        </div>

        {/* Title */}
        <h2
          style={{
            margin: '0 0 0.5rem 0',
            fontSize: 'clamp(1.375rem, 4vw, 1.75rem)',
            fontWeight: 800,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #8BC34A, #CDDC39)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Unlock Montessori Mode
        </h2>

        {/* Subtitle */}
        <p
          style={{
            margin: '0 0 1.25rem 0',
            fontSize: '0.9375rem',
            color: '#64748b',
            textAlign: 'center',
            lineHeight: '1.5',
          }}
        >
          {isSignedIn ? (
            <>
              To use <strong>Montessori features</strong>, please subscribe to{' '}
              <strong>Amaya Plus</strong> or <strong>Amaya Pro</strong>
            </>
          ) : (
            <>
              Montessori Mode is included with <strong>Amaya Plus</strong> and{' '}
              <strong>Amaya Pro</strong> subscriptions
            </>
          )}
        </p>

        {/* Features */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.65rem',
            marginBottom: '1.25rem',
            padding: '1rem',
            background: 'linear-gradient(135deg, rgba(139, 195, 74, 0.05), rgba(205, 220, 57, 0.05))',
            borderRadius: '14px',
            border: '1px solid rgba(139, 195, 74, 0.2)',
          }}
        >
          {[
            'Real-world, calm content inspired by Montessori education',
            'Focus areas: Language, Sensorial, Emotional, Practical Life',
            'Age-appropriate, gentle learning prompts',
            'Natural textures and realistic illustrations',
            'Reflection questions to engage children',
            'Slower, more deliberate narration pacing',
          ].map((feature, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.65rem',
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8BC34A, #CDDC39)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Check size={13} color="white" strokeWidth={3} />
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: '0.875rem',
                  color: '#2c3e50',
                  lineHeight: '1.45',
                }}
              >
                {feature}
              </p>
            </div>
          ))}
        </div>

        {/* Plan Badges */}
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            marginBottom: '1.25rem',
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              padding: '0.625rem',
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 193, 7, 0.1))',
              border: '2px solid rgba(255, 215, 0, 0.4)',
              borderRadius: '10px',
            }}
          >
            <Star size={16} color="#FFD700" fill="#FFD700" />
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#856404' }}>
              Amaya Plus
            </span>
          </div>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              padding: '0.625rem',
              background: 'linear-gradient(135deg, rgba(139, 69, 255, 0.1), rgba(165, 105, 255, 0.1))',
              border: '2px solid rgba(139, 69, 255, 0.4)',
              borderRadius: '10px',
            }}
          >
            <Crown size={16} color="#8B45FF" fill="#8B45FF" />
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#4A148C' }}>
              Amaya Pro
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.625rem',
          }}
        >
          <button
            onClick={handleUpgrade}
            style={{
              width: '100%',
              padding: '0.875rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 700,
              color: 'white',
              background: 'linear-gradient(135deg, #8BC34A, #CDDC39)',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(139, 195, 74, 0.4)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(139, 195, 74, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 195, 74, 0.4)';
            }}
          >
            View Pricing Plans
          </button>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '0.75rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#64748b',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#2c3e50';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#64748b';
            }}
          >
            Maybe Later
          </button>
        </div>

        {/* Legal Copy */}
        <p
          style={{
            margin: '1rem 0 0 0',
            padding: '0.75rem',
            fontSize: '0.6875rem',
            color: '#94a3b8',
            textAlign: 'center',
            lineHeight: '1.35',
            background: 'rgba(0, 0, 0, 0.02)',
            borderRadius: '8px',
          }}
        >
          Montessori is a public educational method. AmayaStories is not affiliated with AMI/AMS;
          we apply Montessori-inspired techniques digitally.
        </p>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
}

